// AnalyticsBootstrap · safe, env-driven Google Analytics 4 loader.
//
// Why a React component instead of an inline script in index.html?
//  · The previous setup shipped a hard-coded placeholder Measurement ID
//    ("G-XXXXXXXXXX") which silently broke production analytics — the
//    GA endpoint accepted the hits but they landed in a property nobody
//    owns, so the Admin → Insights tab showed zero GA data.
//  · CRA only inlines env vars at BUILD time and only into the JS
//    bundle, not into public/index.html. Putting the bootstrap here
//    means the ID is read from process.env.REACT_APP_GA_MEASUREMENT_ID
//    cleanly, and the script is only injected when a real ID is present.
//
// In addition to the initial bootstrap, this component fires a
// `page_view` event on every SPA route change so GA accurately
// reflects single-page navigations (default gtag.js only fires once,
// on the initial document load).
//
// If you ever want to switch from GA4 to GTM, set
// REACT_APP_GTM_CONTAINER_ID instead — GTM takes precedence when both
// are configured, and you manage tags from the GTM dashboard.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const RAW_GA_ID  = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const RAW_GTM_ID = process.env.REACT_APP_GTM_CONTAINER_ID || "";

// Treat the literal placeholder values ("G-XXXXXXXXXX", "GTM-XXXXXXX",
// or anything containing "X" runs) as "not configured" so the very
// common mistake of shipping the unfilled template doesn't dump hits
// into a property that nobody owns. The env var must hold a real ID
// (G-A1B2C3D4E5 / GTM-A1B2C3D) for analytics to actually boot.
const isPlaceholder = (v) => !v || /X{3,}/.test(v);
const GA_ID  = isPlaceholder(RAW_GA_ID)  ? "" : RAW_GA_ID;
const GTM_ID = isPlaceholder(RAW_GTM_ID) ? "" : RAW_GTM_ID;

function injectScript(src) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function loadGTM(containerId) {
  // Standard Google Tag Manager loader · documented at
  // https://developers.google.com/tag-manager/quickstart
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${containerId}`);
}

function loadGA(measurementId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // anonymize_ip is GDPR-friendly · send_page_view false because we
  // dispatch SPA page_views ourselves on every route change below.
  window.gtag("config", measurementId, {
    anonymize_ip: true,
    send_page_view: false,
  });
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
}

export default function AnalyticsBootstrap() {
  const location = useLocation();
  const booted = useRef(false);

  // One-time bootstrap · delays until user interaction OR a 4-second
  // fallback to keep the initial paint fast (gtag.js is ~70KB).
  useEffect(() => {
    if (booted.current) return;
    if (!GA_ID && !GTM_ID) return; // nothing to do · no env var set
    const boot = () => {
      if (booted.current) return;
      booted.current = true;
      if (GTM_ID) loadGTM(GTM_ID);
      if (GA_ID)  loadGA(GA_ID);
    };
    const opts = { once: true, passive: true };
    ["scroll", "mousemove", "touchstart", "keydown", "click"].forEach((ev) =>
      window.addEventListener(ev, boot, opts)
    );
    const timer = setTimeout(boot, 4000);
    return () => clearTimeout(timer);
  }, []);

  // Per-SPA-route page_view · fires on EVERY pathname change including
  // the initial mount, because gtag's auto page_view is disabled in
  // loadGA() above (`send_page_view: false`). Waits a microtask so
  // SEOHead can update document.title first.
  useEffect(() => {
    if (!GA_ID) return;
    const fire = () => {
      if (typeof window.gtag !== "function") return;
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    };
    const id = setTimeout(fire, 50);
    return () => clearTimeout(id);
  }, [location.pathname, location.search]);

  return null;
}
